if (!customElements.get('byte-cart-quote-request')) {
  class ByteCartQuoteRequest extends HTMLElement {
    constructor() {
      super();

      this.isQuoteMode = false;
      this.quoteThreshold = 1000;
      this.cartItemCount = 0;
      this.boundHandleClick = this.handleClick.bind(this);
      this.boundHandleCartUpdate = this.handleCartUpdate.bind(this);
      this.boundHandleEscapeKey = this.handleEscapeKey.bind(this);
      this.boundHandleFormSubmit = this.handleFormSubmit.bind(this);
      this.boundHandleCartFormSubmit = this.handleCartFormSubmit.bind(this);
      this.cartUpdateUnsubscriber = null;
      this.cartForm = null;
    }

    connectedCallback() {
      this.#cacheElements();
      this.#bindEvents();
      this.#updateQuoteMode(this.cartItemCount);
    }

    disconnectedCallback() {
      this.#unbindEvents();
    }

    handleFormSubmit(event) {
      event.preventDefault();
      this.#submitQuoteRequest(event);
    }

    handleCartFormSubmit(event) {
      if (!this.isQuoteMode) return;
      event.preventDefault();
    }

    handleClick(event) {
      const actionTrigger = event.target.closest('[data-quote-action]');
      if (!actionTrigger) return;

      const isInsideComponent = this.contains(actionTrigger);
      const isInsideOverlay = this.overlay?.contains(actionTrigger);
      if (!isInsideComponent && !isInsideOverlay) return;

      const quoteAction = actionTrigger.dataset.quoteAction;

      if (quoteAction === 'open') {
        event.preventDefault();
        this.#openModal();
      }

      if (quoteAction === 'close') {
        event.preventDefault();
        this.#closeModal();
      }

      if (quoteAction === 'submit') {
        event.preventDefault();
        this.#submitQuoteRequest(event);
      }
    }

    handleCartUpdate(event) {
      const itemCount = event?.cartData?.item_count;
      if (typeof itemCount !== 'number') return;

      this.cartItemCount = itemCount;
      this.#updateQuoteMode(itemCount);
    }

    handleEscapeKey(event) {
      if (event.key !== 'Escape') return;
      if (this.overlay?.hasAttribute('hidden')) return;

      this.#closeModal();
    }

    #cacheElements() {
      this.checkoutButton = this.querySelector('[data-cart-checkout]');
      this.quoteButton = this.querySelector('[data-quote-action="open"]');
      this.overlay = this.querySelector('[data-quote-panel]');
      this.form = this.querySelector('.byte-quote-request__form');
      this.successMessage = this.querySelector('[data-quote-success]');
      this.errorMessage = this.querySelector('[data-quote-error]');
      this.emailInput = this.querySelector('[data-quote-email]');
      this.bodyInput = this.querySelector('[data-quote-body-input]');
      this.submitButton = this.querySelector('[data-quote-action="submit"]');
      this.quoteThreshold = Number(this.dataset.quoteThreshold) || 1000;
      this.cartItemCount = Number(this.dataset.cartItemCount) || 0;
    }

    #bindEvents() {
      this.addEventListener('click', this.boundHandleClick);
      document.addEventListener('click', this.boundHandleClick);
      document.addEventListener('keydown', this.boundHandleEscapeKey);
      this.form?.addEventListener('submit', this.boundHandleFormSubmit);

      this.cartForm = document.getElementById('cart');
      this.cartForm?.addEventListener('submit', this.boundHandleCartFormSubmit);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.boundHandleCartUpdate);
      }
    }

    #unbindEvents() {
      this.removeEventListener('click', this.boundHandleClick);
      document.removeEventListener('click', this.boundHandleClick);
      document.removeEventListener('keydown', this.boundHandleEscapeKey);
      this.form?.removeEventListener('submit', this.boundHandleFormSubmit);
      this.cartForm?.removeEventListener('submit', this.boundHandleCartFormSubmit);
      this.cartUpdateUnsubscriber?.();
    }

    #updateQuoteMode(itemCount) {
      const isQuoteMode = itemCount > this.quoteThreshold;

      this.isQuoteMode = isQuoteMode;
      this.classList.toggle('is-quote-mode', isQuoteMode);
      this.dataset.cartItemCount = String(itemCount);

      if (this.checkoutButton) {
        this.checkoutButton.toggleAttribute('disabled', itemCount === 0 || isQuoteMode);
      }

      if (this.quoteButton) {
        this.quoteButton.toggleAttribute('disabled', itemCount === 0);
      }
    }

    #openModal() {
      if (!this.overlay) return;

      if (!this.overlayPlaceholder) {
        this.overlayPlaceholder = document.createComment('byte-cart-quote-request-overlay');
        this.overlay.parentNode.insertBefore(this.overlayPlaceholder, this.overlay);
      }

      document.body.appendChild(this.overlay);
      this.overlay.classList.add('is-portaled');
      this.overlay.removeAttribute('hidden');
      this.overlay.setAttribute('aria-hidden', 'false');
      this.form?.removeAttribute('hidden');
      this.successMessage?.setAttribute('hidden', '');
      this.errorMessage?.setAttribute('hidden', '');
      this.emailInput?.focus();
      document.body.classList.add('overflow-hidden');
    }

    #closeModal() {
      if (!this.overlay) return;

      this.overlay.setAttribute('hidden', '');
      this.overlay.setAttribute('aria-hidden', 'true');
      this.overlay.classList.remove('is-portaled');

      if (this.overlayPlaceholder?.parentNode) {
        this.overlayPlaceholder.parentNode.insertBefore(this.overlay, this.overlayPlaceholder);
      }

      document.body.classList.remove('overflow-hidden');
      this.quoteButton?.focus();
    }

    async #submitQuoteRequest(event) {
      event?.preventDefault();

      const form = this.form || this.overlay?.querySelector('.byte-quote-request__form');
      if (!form) {
        this.#showError(this.dataset.errorSubmit);
        return;
      }

      const email = this.emailInput?.value?.trim();

      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.#showError(this.dataset.errorInvalidEmail);
        return;
      }

      this.#setLoadingState(true);
      this.#showError('');

      try {
        const cartSummary = await this.#buildCartSummary();
        const bodyInput = form.querySelector('[data-quote-body-input]');

        if (bodyInput) {
          bodyInput.value = cartSummary;
        }

        const isSubmitted = await this.#submitContactForm(form);
        if (!isSubmitted) {
          throw new Error('Contact form rejected');
        }

        form.setAttribute('hidden', '');
        this.successMessage?.removeAttribute('hidden');
      } catch (error) {
        this.#showError(this.dataset.errorSubmit);
        console.error(error);
      } finally {
        this.#setLoadingState(false);
      }
    }

    async #buildCartSummary() {
      const cartUrl = window.routes?.cart_url ? `${window.routes.cart_url}.js` : '/cart.js';
      const response = await fetch(cartUrl, {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`Cart fetch failed: ${response.status}`);
      }

      const cart = await response.json();
      const lineItems = Array.isArray(cart.items)
        ? cart.items.map((item) => {
            const variantLabel = item.variant_title ? ` / ${item.variant_title}` : '';
            return `- ${item.product_title}${variantLabel} | Adet: ${item.quantity} | Varyant ID: ${item.variant_id}`;
          })
        : [];

      return [
        'Sepet Teklif Talebi',
        '----------------',
        `Toplam adet: ${cart.item_count || this.cartItemCount}`,
        `Sepet linki: ${window.location.origin}${window.routes?.cart_url || '/cart'}`,
        `Müşteri e-posta: ${this.emailInput?.value?.trim() || ''}`,
        '',
        'Ürünler:',
        ...lineItems,
      ].join('\n');
    }

    #submitContactForm(form) {
      return new Promise((resolve) => {
        const iframeName = `byte-cart-quote-contact-${Date.now()}`;
        const iframe = document.createElement('iframe');
        iframe.name = iframeName;
        iframe.setAttribute('aria-hidden', 'true');
        iframe.style.cssText = 'position:fixed;width:0;height:0;border:0;opacity:0;pointer-events:none';
        document.body.appendChild(iframe);

        let isSettled = false;

        const settle = (isSuccess) => {
          if (isSettled) return;
          isSettled = true;
          form.removeAttribute('target');
          iframe.removeEventListener('load', handleLoad);
          setTimeout(() => iframe.remove(), 1500);
          resolve(isSuccess);
        };

        const handleLoad = () => {
          try {
            const frameDocument = iframe.contentDocument || iframe.contentWindow?.document;
            const frameHtml = frameDocument?.body?.innerHTML || '';
            const frameUrl = iframe.contentWindow?.location?.href || '';

            const hasError =
              frameHtml.includes('form-status caption-large text-body') ||
              frameHtml.includes('contact__field-error') ||
              frameHtml.includes('error_heading');

            const hasSuccess =
              frameUrl.includes('contact_posted=true') ||
              frameHtml.includes('post_success') ||
              frameHtml.includes('form-status-list form__message') ||
              frameHtml.includes('Bizimle iletişime geçtiğiniz için') ||
              frameHtml.includes('teşekkür ederiz');

            settle(hasSuccess && !hasError);
          } catch {
            settle(true);
          }
        };

        iframe.addEventListener('load', handleLoad);
        form.target = iframeName;
        form.submit();

        setTimeout(() => settle(true), 4000);
      });
    }

    #showError(message) {
      if (!this.errorMessage) return;

      if (!message) {
        this.errorMessage.setAttribute('hidden', '');
        this.errorMessage.textContent = '';
        return;
      }

      this.errorMessage.textContent = message;
      this.errorMessage.removeAttribute('hidden');
    }

    #setLoadingState(isLoading) {
      if (!this.submitButton) return;

      this.submitButton.classList.toggle('loading', isLoading);
      this.submitButton.toggleAttribute('disabled', isLoading);
      this.submitButton.setAttribute('aria-disabled', String(isLoading));
    }
  }

  customElements.define('byte-cart-quote-request', ByteCartQuoteRequest);
}
